/**
 * Map Handler Utilities
 * 
 * Shared handler functions used by both HomePageMap and EntityDetailMap.
 * Extracted to eliminate code duplication and improve maintainability.
 * 
 * @module utils/mapHandlerUtils
 */

import { toast } from '../services/toastService'
import { trackPanelStateChange } from './analytics'
import { createLogger } from './logger'

const logger = createLogger('MapHandlerUtils')

// Constants for geolocation behavior
const GEOLOCATION_ANIMATION_DURATION = 1.5 // seconds - smooth animation for better UX
const BOUNDS_FIT_SKIP_DURATION = 2000 // milliseconds - prevents MapBoundsFitter from interfering (must be > animation duration)
const ANIMATION_BUFFER = 100 // milliseconds - buffer after animation completes
const MY_LOCATION_ZOOM = 16 // zoom level for My Location button - close view for better context

/**
 * Center map at location with optional zoom level
 * @param {Object} mapRef - Leaflet map reference
 * @param {number} latitude - Target latitude
 * @param {number} longitude - Target longitude
 * @param {boolean} preventDragHandler - Whether to prevent MapDragHandler from triggering
 * @param {number|null} targetZoom - Target zoom level (null = preserve current zoom)
 */
const centerMapAtZoom = (mapRef, latitude, longitude, preventDragHandler = false, targetZoom = null) => {
  if (!mapRef.current) return
  
  const zoomLevel = targetZoom !== null ? targetZoom : mapRef.current.getZoom()
  
  // Always use flyTo for smooth zoom animation
  if (preventDragHandler) {
    // Mark as programmatic move to prevent MapDragHandler from triggering
    mapRef.current._programmaticMove = true
  }
  
  mapRef.current.flyTo([latitude, longitude], zoomLevel, {
    duration: GEOLOCATION_ANIMATION_DURATION
  })
  
  // Reset flag after animation completes (if needed)
  if (preventDragHandler) {
    const resetDelay = GEOLOCATION_ANIMATION_DURATION * 1000 + ANIMATION_BUFFER
    setTimeout(() => {
      if (mapRef.current) mapRef.current._programmaticMove = false
    }, resetDelay)
  }
}

/**
 * Handle share functionality (native share or clipboard fallback)
 * @param {Object} options - Share options
 * @param {string} options.url - URL to share
 * @param {string} options.title - Share title
 * @param {string} options.text - Share text
 * @param {string|null} options.platform - Platform-specific share ('twitter', 'facebook', 'linkedin', or null for native)
 * @param {Function} options.t - Translation function
 * @param {string|null} options.entityId - Entity ID for UTM content attribution
 */
export const handleShareURL = async ({ url, title, text, platform, t, entityId }) => {
  // Append UTM tracking parameters
  const medium = platform || 'native'
  const utmUrl = (() => {
    try {
      const u = new URL(url)
      u.searchParams.set('utm_source', 'share')
      u.searchParams.set('utm_medium', medium)
      u.searchParams.set('utm_campaign', 'entity-share')
      if (entityId) u.searchParams.set('utm_content', entityId)
      return u.toString()
    } catch {
      return url
    }
  })()
  if (platform) {
    // Platform-specific sharing
    let shareUrl
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(utmUrl)}`
        break
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(utmUrl)}`
        break
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(utmUrl)}`
        break
      default:
        return
    }
    window.open(shareUrl, '_blank', 'noopener,noreferrer')
  } else {
    // Native share or clipboard fallback
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: utmUrl })
        toast.success(t('entities.detail.shareSuccess') || 'Shared successfully!')
      } catch (err) {
        if (err.name !== 'AbortError') {
          logger.error('Share failed', err)
        }
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(utmUrl)
        toast.success(t('entities.detail.linkCopied') || 'Link copied to clipboard!')
      } catch (err) {
        logger.error('Copy to clipboard failed', err)
        toast.error(t('entities.detail.shareFailed') || 'Failed to share')
      }
    }
  }
}

/**
 * Create a handlePanelStateChange callback with analytics tracking
 * @param {string} currentPanelState - Current panel state
 * @param {string} activeTab - Current active tab
 * @param {boolean} isImmersiveMode - Whether in immersive mode
 * @param {Function} onTabChange - Tab change callback
 * @param {string} source - Source component name for tracking
 * @returns {Function} Panel state change handler
 */
export const createPanelStateChangeHandler = (
  currentPanelState, 
  activeTab, 
  isImmersiveMode, 
  onTabChange,
  source
) => {
  return (newState) => {
    trackPanelStateChange(currentPanelState, newState, activeTab, 'button_click', source)
    
    // Update panel state through parent's onTabChange if in immersive mode
    if (isImmersiveMode && onTabChange) {
      onTabChange(activeTab, newState)
    }
  }
}

/**
 * Handle current location button click
 * @param {Object} options - Handler options
 * @param {Function} options.getUserLocation - Get user location function
 * @param {Function} options.setUserLocation - Set user location function
 * @param {Function} options.setSearchLocation - Set search location function (optional)
 * @param {Function} options.setSearchLocationName - Set search location name function (optional)
 * @param {Function} options.fetchCallback - Fetch entities callback (optional for entity mode)
 * @param {Function} options.setLocationSource - Set location source function (optional)
 * @param {Function} options.setIsGettingLocation - Set loading state function
 * @param {Object} options.mapRef - Map reference
 * @param {boolean} options.isEntityMode - Whether in entity mode
 * @param {boolean} options.isGeolocationAvailable - Whether geolocation is available
 * @param {boolean} options.isGettingLocation - Whether currently getting location
 * @param {Function} options.saveLocation - Persist location to localStorage (optional)
 */
export const handleCurrentLocationClick = ({
  getUserLocation,
  setUserLocation,
  setSearchLocation,
  setSearchLocationName,
  setShowUserLocation,
  setIsSearchPanelMinimized,
  onPanelMinimize,
  fetchCallback,
  setLocationSource,
  setIsGettingLocation,
  saveLocation,
  mapRef,
  isEntityMode = false,
  isGeolocationAvailable = true,
  isGettingLocation = false
}) => {
  // Don't allow if geolocation not supported
  if (!isGeolocationAvailable) {
    return
  }

  // Prevent duplicate requests if already getting location
  if (isGettingLocation) {
    return
  }

  if (isEntityMode) {
    // Entity mode: just recenter map without fetching
    getUserLocation({
      onSuccess: (location) => {
        setUserLocation(location)
        // Show user location marker
        if (setShowUserLocation) setShowUserLocation(true)
        // Zoom in to user's location for better context
        centerMapAtZoom(mapRef, location.latitude, location.longitude, false, MY_LOCATION_ZOOM)
      },
      onError: () => {},
      hasShownErrorRef: { current: false }
    })
  } else {
    // Normal mode: set loading state and fetch entities
    setIsGettingLocation(true)
    
    // Minimize panel for better map visibility
    if (onPanelMinimize) {
      onPanelMinimize()
    } else if (setIsSearchPanelMinimized) {
      setIsSearchPanelMinimized(true)
    }
    
    // Preserve current zoom before fetching to prevent zoom-out when new markers load
    const currentZoom = mapRef.current?.getZoom()
    if (currentZoom && mapRef.current) {
      // Tell MapBoundsFitter to skip automatic bounds fitting temporarily
      mapRef.current._skipFitBoundsUntil = Date.now() + BOUNDS_FIT_SKIP_DURATION
    }
    
    // Small delay to ensure UI updates before browser permission dialog appears
    setTimeout(() => {
      getUserLocation({
        onSuccess: (location, locationName) => {
          setUserLocation(location)
          // Show user location marker
          if (setShowUserLocation) setShowUserLocation(true)
          if (setSearchLocation) setSearchLocation(locationName)
          if (setSearchLocationName) setSearchLocationName(locationName)
          // Persist to localStorage so back-navigation restores the GPS location
          if (saveLocation) saveLocation({ latitude: location.latitude, longitude: location.longitude, name: locationName })
          
          // Center map and zoom in to user location for better context
          centerMapAtZoom(mapRef, location.latitude, location.longitude, true, MY_LOCATION_ZOOM)
          
          if (fetchCallback) fetchCallback(location)
          if (setLocationSource) setLocationSource('geolocation')
          setIsGettingLocation(false)
        },
        onError: () => {
          setIsGettingLocation(false)
        },
        hasShownErrorRef: { current: false }
      })
    }, 10)
  }
}
